import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { adminPublicDemoApi, type AdminDemoVisitorRow } from '../../api/admin.ts';
import { Alert, Button, LoadingBlock, Pagination, Select, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { DemoInvitesSection } from './public-demo-invites.tsx';
import { formatNumericInstant } from '../../i18n/datetime.ts';

/**
 * Live demo — the leads, and the three levers over the company they walked into.
 *
 * THE LEAD LIST IS THE POINT OF THIS PAGE. Everyone who has opened /try typed a
 * name, a work email and a company name to do it, and until this screen existed
 * those answers sat in a table nobody could read without a SQL client. They are
 * the warmest leads the business has — somebody who went and looked — and the
 * whole purpose here is that a person can see them, sort out who asked to be
 * contacted, and act.
 *
 * WHAT AN OPERATOR MAY DO WITH A ROW, which is the thing this screen has to get
 * right, because the answer is not the same for every row:
 *
 *  - "Emails: yes" — they ticked the opt-in box on /try AND have not since
 *    unsubscribed. They are in the demo nurture campaign, and may be written to.
 *  - "Emails: no" — they were told, on the page, in bold, that opening the demo
 *    signs them up for nothing and that we would not email them at all. So they
 *    are not to be emailed: not a campaign, and not a personal note from one of
 *    us either. This screen is where you SEE them, not a list to write to.
 *  - "Emails: withdrawn" — they agreed, we wrote to them, and they clicked
 *    unsubscribe. Treated exactly as a "no", and shown as its own word rather
 *    than folded into one: an operator who sees "no" beside a lead they
 *    remember emailing last week should be told why it changed, not left to
 *    wonder whether the screen is wrong.
 *
 * That third state is not cosmetic. Without it this screen went on printing
 * "yes" for somebody who had unsubscribed — the site owns the campaign and
 * stopped it, and nothing told the app — so the one screen an operator consults
 * before writing to a lead was the one place that had not heard.
 *
 * That second line used to say a person could write to them individually, and
 * it was wrong — it contradicted the form copy and the privacy policy, both of
 * which promise an unticked box means no email at all. The distinction the
 * column draws is only worth anything if it means one thing everywhere, so it
 * now means the same thing here, on the form, and in content/legal/privacy.md.
 *
 * The column says which, in those words, because a CSV export of this table
 * with a "consent" column full of timestamps is exactly how that distinction
 * gets lost. The filter defaults to showing everybody: hiding the people who
 * said no would make the list look like a mailing list, which is the mistake.
 *
 * The three levers below it are unchanged and documented on the API side
 * (routes/admin-public-demo.ts): rebuild the company now, close the door, and
 * refuse one address.
 */

const PAGE_SIZE = 50;

/** The channel, or the referring host when the visit had no utm tag. */
function sourceLabel(row: AdminDemoVisitorRow): string {
  const source = row.attribution;
  if (!source) return '—';
  if (source.utmSource) return source.utmSource;
  if (source.referrer) {
    try {
      return new URL(source.referrer).hostname.replace(/^www\./, '');
    } catch {
      return source.referrer;
    }
  }
  return source.landingPath || '—';
}

function sourceDetail(row: AdminDemoVisitorRow): string {
  const source = row.attribution;
  if (!source) return '';
  return [source.utmSource, source.utmMedium, source.utmCampaign, source.landingPath, source.referrer]
    .filter(Boolean)
    .join(' · ');
}

export default function AdminPublicDemoPage() {
  useDocumentTitle('Live demo');
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const optedIn = params.get('optedIn') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [blockEmail, setBlockEmail] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);

  const setParam = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    if (!('page' in updates)) next.delete('page');
    setParams(next, { replace: true });
  };

  const state = useQuery({
    queryKey: ['admin', 'public-demo', 'state'],
    queryFn: ({ signal }) => adminPublicDemoApi.state(signal),
  });

  const visitors = useQuery({
    queryKey: ['admin', 'public-demo', 'visitors', { q: params.get('q'), optedIn, page }],
    queryFn: ({ signal }) =>
      adminPublicDemoApi.visitors(
        {
          ...(params.get('q') ? { search: params.get('q')! } : {}),
          ...(optedIn ? { optedIn } : {}),
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        signal,
      ),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'public-demo'] });
  };

  const rebuild = useMutation({
    mutationFn: adminPublicDemoApi.rebuild,
    onSuccess: () => {
      setNotice({ tone: 'success', message: 'The demo company is being rebuilt. It takes a minute or two.' });
      refresh();
    },
    onError: (error: Error) => setNotice({ tone: 'error', message: error.message }),
  });

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => adminPublicDemoApi.setEnabled(enabled),
    onSuccess: (result) => {
      setNotice({
        tone: 'success',
        message: result.enabled
          ? 'The demo is open. New visitors can come in.'
          : 'The demo is closed to new visitors. Everybody already inside stays there.',
      });
      refresh();
    },
    onError: (error: Error) => setNotice({ tone: 'error', message: error.message }),
  });

  const block = useMutation({
    mutationFn: () => adminPublicDemoApi.block(blockEmail.trim(), blockReason.trim() || 'operator'),
    onSuccess: () => {
      setNotice({ tone: 'success', message: `${blockEmail.trim()} is refused at the door from now on.` });
      setBlockEmail('');
      setBlockReason('');
      refresh();
    },
    onError: (error: Error) => setNotice({ tone: 'error', message: error.message }),
  });

  // A failed refetch retains the last visitor list in React Query's cache.
  // Consent can change between reads, so do not display cached "Emails: yes"
  // labels while the current read is pending or failed.
  const currentVisitors = visitors.isSuccess && !visitors.isFetching ? visitors.data : undefined;
  const total = currentVisitors?.total;
  const totalPages = total === undefined ? undefined : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const live = state.data?.tenants.find((tenant) => tenant.status === 'live');

  return (
    <div className="portal-page">
      <WorkPage
        title="Live demo"
        description="Everyone who has walked into the shared demo at /try, and the controls over the company they walked into."
      >
        {notice ? (
          <div className="mb-4">
            <Alert tone={notice.tone === 'success' ? 'success' : 'error'}>{notice.message}</Alert>
          </div>
        ) : null}

        {state.isLoading ? <LoadingBlock label="Loading demo controls…" /> : null}
        {state.isError ? (
          <div className="mb-6">
            <Alert tone="error" title="The demo controls could not be loaded">
              <Button variant="ghost" onClick={() => void state.refetch()}>
                Try again
              </Button>
            </Alert>
          </div>
        ) : null}

        {state.data && !state.data.available ? (
          <div className="mb-6">
            <Alert tone="info" title="This deployment runs no public demo">
              The demo needs a database of its own (HYPERDRIVE_SHARD_DEMO). Nothing below will show anything until it is
              bound.
            </Alert>
          </div>
        ) : null}

        {state.isSuccess && state.data.available ? (
          <div className="card mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-sm">
                <span className="font-semibold">{state.data.enabled ? 'Open' : 'Closed to new visitors'}</span>
                {live ? ` · generation ${live.generation}` : ' · no live company'}
                {state.data.nextResetAt ? ` · next rebuild ${formatNumericInstant(state.data.nextResetAt)}` : ''}
              </p>
              <p className="mt-1 text-xs text-[color:var(--color-muted)]">
                Closing the door refuses new arrivals. It throws nobody out and deletes nothing.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={setEnabled.isPending}
                onClick={() => setEnabled.mutate(!state.data!.enabled)}
              >
                {state.data.enabled ? 'Close the door' : 'Open the door'}
              </Button>
              <Button variant="secondary" disabled={rebuild.isPending} onClick={() => rebuild.mutate()}>
                {rebuild.isPending ? 'Rebuilding…' : 'Rebuild now'}
              </Button>
            </div>
          </div>
        ) : null}

        <form
          className="card mb-6 grid gap-4 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            setParam({ q: search.trim() || undefined });
          }}
        >
          <div className="sm:col-span-2 flex items-end gap-2">
            <TextInput
              label="Search"
              value={search}
              placeholder="name, work email or company"
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="mb-4">
              <Button type="submit">Search</Button>
            </div>
          </div>
          <Select
            label="Marketing emails"
            value={optedIn}
            options={[
              { value: '', label: 'Everyone who has been in' },
              { value: 'true', label: 'Opted in only' },
            ]}
            onChange={(event) => setParam({ optedIn: event.target.value || undefined })}
          />
        </form>

        {visitors.isError ? (
          <div className="mb-4">
            <Alert tone="error" title="The visitor list could not be loaded">
              Check that the demo database has been migrated.{' '}
              <Button variant="ghost" onClick={() => void visitors.refetch()}>
                Try again
              </Button>
            </Alert>
          </div>
        ) : null}

        {visitors.isFetching ? <LoadingBlock label="Loading visitors…" /> : null}

        {currentVisitors && currentVisitors.visitors.length === 0 ? (
          <WorkEmpty title="Nobody matches that">
            Visitors are forgotten automatically ninety days after their last visit, so an old address will not be here.
          </WorkEmpty>
        ) : null}

        {/* Mounted whenever the total is KNOWN, including for an empty page —
          which is the case it exists for. `Pagination` walks a reader back when
          `page > totalPages`, and that correction can only run if the component
          is rendered; gating it on "this response has rows" meant the one
          situation needing it (retention shrank the list while somebody sat on
          its last page) was the one situation where it never mounted, leaving
          them on "Nobody matches that" with no way back but the URL bar. */}
        {total !== undefined && currentVisitors && currentVisitors.visitors.length === 0 ? (
          <Pagination page={page} totalPages={totalPages} onPageChange={(next) => setParam({ page: String(next) })} />
        ) : null}

        {currentVisitors && currentVisitors.visitors.length > 0 ? (
          <>
            <p className="mb-2 text-xs text-[color:var(--color-muted)]">
              {total} visitor{total === 1 ? '' : 's'} who have actually been in. A row marked{' '}
              <strong>Emails: no</strong> was told on the /try page, in bold, that opening the demo signs them up for
              nothing and that we would not email them — so please do not, not even a personal note.{' '}
              <strong>Withdrawn</strong> means they were opted in and have since unsubscribed, which is the same answer
              for a stronger reason. They are here to be seen, not written to.
            </p>
            <TableScroll aria-label="Demo visitors" className="overflow-x-auto">
              <table className="data-table w-full min-w-max text-sm">
                <thead>
                  <tr className="border-b border-[color:var(--color-line)] text-left">
                    <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                      Visitor
                    </th>
                    <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                      Company
                    </th>
                    <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                      Emails
                    </th>
                    <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                      Source
                    </th>
                    <th scope="col" className="py-2 pr-4 text-right whitespace-nowrap">
                      Visits
                    </th>
                    <th scope="col" className="py-2 pr-4 text-right whitespace-nowrap">
                      AI credits
                    </th>
                    <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                      Last seen
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentVisitors.visitors.map((row) => (
                    <tr key={row.id} className="border-b border-[color:var(--color-line)] align-top">
                      <td className="py-3 pr-4">
                        <span className="block max-w-[18rem] table-text font-semibold">{row.fullName}</span>
                        <span className="block max-w-[18rem] table-text text-xs text-[color:var(--color-muted)]">
                          {row.email}
                          {row.language === 'es' ? ' · es' : ''}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <span className="block max-w-[16rem] table-text">{row.companyName}</span>
                        {row.blockedAt ? (
                          <span className="block max-w-[16rem] table-text text-xs text-[color:var(--color-danger,#b4402f)]">
                            blocked · {row.blockedReason ?? 'operator'}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        {row.marketingOptOutAt ? (
                          <>
                            <span className="font-semibold text-[color:var(--color-muted)]">withdrawn</span>
                            <span className="block text-xs text-[color:var(--color-muted)]">
                              unsubscribed {formatNumericInstant(row.marketingOptOutAt)}
                            </span>
                          </>
                        ) : row.marketingOptInAt ? (
                          <>
                            <span className="font-semibold">yes</span>
                            <span className="block text-xs text-[color:var(--color-muted)]">
                              asked {formatNumericInstant(row.marketingOptInAt)}
                            </span>
                          </>
                        ) : (
                          <span className="text-[color:var(--color-muted)]">no</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap" title={sourceDetail(row)}>
                        <span className="block max-w-[12rem] table-text">{sourceLabel(row)}</span>
                      </td>
                      <td className="tabular py-3 pr-4 text-right whitespace-nowrap">{row.entryCount}</td>
                      <td className="tabular py-3 pr-4 text-right whitespace-nowrap">{row.creditsUsed}</td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        {formatNumericInstant(row.lastEnteredAt ?? row.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <Pagination page={page} totalPages={totalPages} onPageChange={(next) => setParam({ page: String(next) })} />
          </>
        ) : null}

        {state.isSuccess && state.data.available ? <DemoInvitesSection /> : null}

        <form
          className="card mt-8 grid gap-4 sm:grid-cols-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (blockEmail.trim()) block.mutate();
          }}
        >
          <div className="sm:col-span-3">
            <h2 className="font-semibold">Refuse an address</h2>
            <p className="mt-1 text-xs text-[color:var(--color-muted)]">
              For the one visitor who keeps finding a way around the automatic limits. They are refused on the way in;
              any session they already have is left alone.
            </p>
          </div>
          <TextInput
            label="Work email"
            value={blockEmail}
            placeholder="someone@example.com"
            onChange={(event) => setBlockEmail(event.target.value)}
          />
          <TextInput
            label="Reason"
            value={blockReason}
            placeholder="operator"
            onChange={(event) => setBlockReason(event.target.value)}
          />
          <div className="flex items-end">
            <div className="mb-4">
              <Button type="submit" variant="secondary" disabled={block.isPending || !blockEmail.trim()}>
                {block.isPending ? 'Blocking…' : 'Block'}
              </Button>
            </div>
          </div>
        </form>
      </WorkPage>
    </div>
  );
}
